import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { NineDotMark } from "./NineDotLogo";
import { Countdown } from "./Countdown";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
    <span className={`inline-block overflow-hidden align-bottom ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 180, damping: 18 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 180, damping: 18 });

    const onMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };
    const onLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <section
            data-testid="hero-section"
            className="relative flex shrink-0 flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border border-slate-200/80 bg-white px-4 py-4 shadow-sm sm:flex-row sm:justify-between sm:gap-4 sm:px-8 sm:py-5"
        >
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(520px 220px at 12% 50%, rgba(13,148,136,0.08), transparent 65%)",
                }}
            />

            <div className="relative flex items-center gap-4 sm:gap-6">
                <motion.div
                    ref={ref}
                    onMouseMove={onMove}
                    onMouseLeave={onLeave}
                    style={{ perspective: 800, rotateX, rotateY, transformStyle: "preserve-3d" }}
                    className="inline-block cursor-default"
                    data-testid="hero-interactive-9dot-logo"
                >
                    <NineDotMark size={0.55} className="sm:hidden" />
                    <NineDotMark size={0.8} className="hidden sm:block" />
                </motion.div>
                <div className="text-left">
                    <h1
                        data-testid="hero-title-text"
                        className="font-heading text-2xl font-extrabold leading-none tracking-tight text-slate-900 sm:text-3xl lg:text-4xl"
                    >
                        <MaskedLine delay={0.45}>Clear</MaskedLine>{" "}
                        <MaskedLine delay={0.58}>
                            <span className="text-teal-600">D</span>
                            <span className="text-slate-300">.</span>
                        </MaskedLine>
                    </h1>
                    <motion.p
                        data-testid="hero-motto-text"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.85, duration: 0.5 }}
                        className="mt-1 font-heading text-xs font-semibold text-slate-600 sm:text-sm"
                    >
                        Your Health, Our Priority.
                    </motion.p>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.95, duration: 0.5 }}
                className="relative flex flex-col items-center gap-1.5 sm:items-end"
            >
                <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-60" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" />
                    </span>
                    <span className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                        Launching 26 · 12 · 2027
                    </span>
                </div>
                <Countdown />
            </motion.div>

            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="relative hidden max-w-[240px] text-right font-body text-xs leading-relaxed text-slate-400 lg:block xl:max-w-[300px]"
            >
                A transparent B2B online platform aggregator by Clear Healthcare Private Limited
            </motion.p>
        </section>
    );
};
